import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Copy, RefreshCw, ExternalLink, CheckCircle, Link,
  ClipboardList, DollarSign, RotateCcw, Target, Phone, Settings2
} from 'lucide-react';

const CopyField = ({ label, value, icon: Icon }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={13} className="text-slate-400" />}
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 font-mono break-all">
          {value}
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1 flex-shrink-0" onClick={copy}>
          {copied ? <><CheckCircle size={12} className="text-green-600" /> Copied</> : <><Copy size={12} /> Copy</>}
        </Button>
        <a href={value} target="_blank" rel="noopener noreferrer">
          <Button size="sm" variant="outline" className="h-8 w-8 p-0 flex-shrink-0">
            <ExternalLink size={12} />
          </Button>
        </a>
      </div>
    </div>
  );
};

export default function SettingsPage() {
  const [formLinks, setFormLinks] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const navigate = useNavigate();

  const BASE_URL = window.location.origin;

  const fetchAll = async () => {
    try {
      const [linksRes, onbRes] = await Promise.all([
        axios.get(`${API}/settings/form-links`),
        axios.get(`${API}/settings/onboarding`),
      ]);
      setFormLinks(linksRes.data);
      setOnboarding(onbRes.data);
    } catch (err) { console.error("[Settings] Failed to load:", err); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate form link? The old link will stop working immediately.')) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/settings/regenerate-form-token`);
      setFormLinks(prev => ({ ...prev, form_token: res.data.form_token }));
      toast.success('Form link regenerated');
    } catch { toast.error('Failed to regenerate'); }
    setRegenerating(false);
  };

  if (loading) return <div className="text-slate-400 text-sm p-6">Loading settings...</div>;

  const token = formLinks?.form_token;
  const formsBase = `${BASE_URL}/forms/${token}`;
  const eodLink = `${BASE_URL}/forms/${token}/eod`;
  const callCenterLink = `${BASE_URL}/forms/${token}/callcenter`;
  const saleLink = `${BASE_URL}/forms/${token}/sale`;

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <Settings2 size={22} className="text-cyan-400" /> Settings
        </h1>
        <p className="text-slate-500 text-sm mt-1">Manage your form links, monthly goals, and account settings</p>
      </div>

      {/* Public Form Links */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Link size={16} className="text-cyan-500" /> Staff Form Links
              </CardTitle>
              <p className="text-xs text-slate-400 mt-0.5">
                Share these links with your staff so they can submit data without logging in
              </p>
            </div>
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={handleRegenerate} disabled={regenerating}>
              <RefreshCw size={12} className={regenerating ? 'animate-spin' : ''} /> Regenerate
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <CopyField label="Main Form Page (Staff Landing)" value={formsBase} icon={ExternalLink} />
          <Separator />
          <CopyField label="EOD Summary — Sales (Direct Link)" value={eodLink} icon={ClipboardList} />
          <Separator />
          <CopyField label="EOD Summary — Call Center (Direct Link)" value={callCenterLink} icon={Phone} />
          <Separator />
          <CopyField label="Sale Entry Form (Direct Link)" value={saleLink} icon={DollarSign} />
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
            <strong>⚠️ Important:</strong> These links are public — anyone with the link can submit data. Use “Regenerate” if a link is compromised. New link takes effect immediately.
          </div>
        </CardContent>
      </Card>

      {/* Onboarding Progress */}
      {onboarding && !onboarding.onboarding_completed && (
        <Card className="bg-white shadow-sm" style={{ borderLeft: '3px solid hsl(191,95%,43%)' }}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <CheckCircle size={16} className="text-cyan-500" /> Getting Started Checklist
              </CardTitle>
              <button
                onClick={() => {
                  const key = `onboarding_dismissed_${onboarding?.user_id || 'unknown'}`;
                  localStorage.removeItem(key);
                  toast.success('Onboarding checklist will appear on next page load');
                }}
                className="text-xs text-cyan-500 hover:text-cyan-700 flex items-center gap-1"
              >
                <RotateCcw size={12} /> Re-open checklist
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { key: 'has_location', label: 'Add your first location', path: '/admin/locations' },
                { key: 'has_team_member', label: 'Add a team member', path: '/admin/users' },
                { key: 'has_form_token', label: 'Get your form links', path: null },
                { key: 'has_eod', label: 'Submit your first EOD', path: '/eod/new' },
                { key: 'has_sale', label: 'Record your first sale', path: '/sales/new' },
              ].map(step => {
                const done = onboarding.checks?.[step.key];
                return (
                  <div key={step.key} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        done ? 'bg-green-100' : 'bg-slate-100'
                      }`}>
                        {done ? <CheckCircle size={12} className="text-green-600" />
                          : <span className="w-2 h-2 rounded-full bg-slate-300" />}
                      </div>
                      <span className={`text-sm ${done ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                        {step.label}
                      </span>
                    </div>
                    {!done && step.path && (
                      <Button size="sm" variant="outline" className="h-6 text-xs"
                        onClick={() => navigate(step.path)}>Go →</Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Goals shortcut */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Target size={16} className="text-cyan-500" /> Monthly Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-3">
            Set monthly targets for FA Presented, Close Rate, Revenue, and more. Dashboard auto-prorates to your selected date range.
          </p>
          <Button onClick={() => navigate('/goals')} variant="outline" className="gap-2">
            <Target size={14} /> Manage Monthly Goals →
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
